import { graphql } from 'gql.tada'

export const SalesmensListDocument = graphql(`
  query SalesmensList($limit: Int!, $offset: Int!) {
    salesmens(
      limit: $limit
      offset: $offset
      order_by: { id: desc }
    ) {
      id
      first_name
      last_name
      tag_color
      company {
        id
        name
      }
    }
    salesmens_aggregate {
      aggregate {
        count
      }
    }
  }
`)

// The whole roster in one shot, for the company (SOCIETY) hub list, which
// filters, searches and paginates client-side the way `/clients` does. Unpaged
// on purpose: a company's roster is its own commerciaux only (the SOCIETY
// select filter on `salesmens` is `company.accounts.id = X-Hasura-User-Id`), so
// it is a handful of rows. The architect's register is `SalesmensRegister`
// below; the paginated `SalesmensList` above feeds the salesman pickers.
export const SalesmensHubListDocument = graphql(`
  query SalesmensHubList {
    salesmens(order_by: { id: desc }) {
      id
      first_name
      last_name
      phone
      tag_color
      created
      account {
        id
        email
        active
      }
    }
  }
`)

// The architect's register: every company's commerciaux, with the company each
// belongs to — the column that tells them apart. Unpaged like the company's
// roster above: the hub list filters, searches and paginates client-side.
export const SalesmensRegisterDocument = graphql(`
  query SalesmensRegister {
    salesmens(order_by: { id: desc }) {
      id
      first_name
      last_name
      phone
      tag_color
      created
      account {
        id
        email
        active
      }
      company {
        id
        name
      }
    }
  }
`)

// The signed-in company's own row. No filter is needed: the SOCIETY select
// permission on `companies` is `accounts.id = X-Hasura-User-Id`, so this
// resolves to exactly one row — the caller's. Used to fill `society_id` when a
// company adds a salesman, since that FK points at `companies.id`, which the
// session variable does not carry (#379).
export const OwnCompanyDocument = graphql(`
  query OwnCompany {
    companies(limit: 1) {
      id
    }
  }
`)

export const SalesmanOneDocument = graphql(`
  query SalesmanOne($id: Int!) {
    salesmens_by_pk(id: $id) {
      id
      first_name
      last_name
      gender
      phone
      tag_color
      account {
        id
        active
        email
      }
      company {
        id
        name
      }
    }
  }
`)

export const SalesmanInsertDocument = graphql(`
  mutation SalesmanInsert($object: salesmens_insert_input!) {
    insert_salesmens_one(object: $object) {
      id
    }
  }
`)

export const SalesmanUpdateDocument = graphql(`
  mutation SalesmanUpdate($id: Int!, $set: salesmens_set_input!) {
    update_salesmens_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
    }
  }
`)

export const GenderOptionsDocument = graphql(`
  query GenderOptions {
    gender {
      value
      title
    }
  }
`)

export const CompaniesOptionsDocument = graphql(`
  query CompaniesOptions {
    companies(order_by: { name: asc }) {
      id
      name
    }
  }
`)

// Direct sign-in-email change (FR-002/FR-004). ARCHITECT has update on
// accounts.email; the accounts_email_key UNIQUE constraint is authoritative
// on duplicates.
export const AccountEmailUpdateDocument = graphql(`
  mutation AccountEmailUpdate($id: Int!, $email: String!) {
    update_accounts_by_pk(
      pk_columns: { id: $id }
      _set: { email: $email }
    ) {
      id
      email
    }
  }
`)

// Activate / disable a linked account. ARCHITECT has update on accounts.active.
export const AccountActiveUpdateDocument = graphql(`
  mutation AccountActiveUpdate($id: Int!, $active: Boolean!) {
    update_accounts_by_pk(
      pk_columns: { id: $id }
      _set: { active: $active }
    ) {
      id
      active
    }
  }
`)

// Uniqueness pre-check for the edit-page *Create account* gate (FR-019) and
// email-change UX (FR-003). Advisory; the DB constraint stays authoritative.
// Case-insensitive: `$pattern` is `emailMatchPattern(email)`.
export const AccountEmailExistsDocument = graphql(`
  query AccountEmailExists($pattern: String!) {
    accounts(where: { email: { _ilike: $pattern } }, limit: 1) {
      id
    }
  }
`)

// The roster offered when a dossier or a study changes hands (#769).
//
// No filter of its own beyond `enabled`: the `salesmens` select permission
// already decides the scope — a company sees its own commerciaux
// (`company.accounts.id = X-Hasura-User-Id`), the office sees every one — so
// the picker cannot offer a name the server would then refuse. A disabled
// commercial is left out because the action refuses them anyway; offering one
// would be an option that can only fail.
export const SalesmanAssignmentCandidatesDocument = graphql(`
  query SalesmanAssignmentCandidates {
    salesmens(
      where: { enabled: { _eq: true } }
      order_by: [{ last_name: asc }, { first_name: asc }]
    ) {
      id
      first_name
      last_name
      company {
        id
        name
      }
    }
  }
`)

// One commercial by id, to name the one a study currently carries (#769).
//
// Its own document rather than a `salesman { … }` branch on
// `UrbanAnalysisOneDocument`: that query feeds the editor's antd form store,
// and `useUrbanAnalysisEdit` autosaves the whole store back as an
// `urban_analysis_set_input` — a nested relation there would be posted as a
// column and fail every save.
export const SalesmanNameDocument = graphql(`
  query SalesmanName($id: Int!) {
    salesmens_by_pk(id: $id) {
      id
      first_name
      last_name
    }
  }
`)
